// src/components/ResponsiveSidebar.jsx - Updated with Worker Dashboard
import React, { useState, useEffect } from 'react';
import { Menu, X, BarChart, Film, Upload, Settings, LogOut, Moon, Sun, Server, CreditCard } from 'lucide-react';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

const SidebarLink = ({ icon, text, active, onClick, badge = null }) => {
  return (
    <button 
      className={`flex items-center w-full px-6 py-3 text-left transition-colors relative
                ${active 
                  ? 'bg-gray-800 text-white' 
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
      onClick={onClick}
    >
      <span className="mr-3">{icon}</span>
      <span className="text-sm">{text}</span>
      {badge && (
        <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
};

const ResponsiveSidebar = ({ activeTab, setActiveTab, user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [workerStatus, setWorkerStatus] = useState(null);
  const { t } = useLanguage();

  // Track window width for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Auto-close sidebar on mobile when window resizes to larger size
      if (window.innerWidth >= 1024 && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Check worker status periodically
  useEffect(() => {
    const checkWorkerStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/health', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (response.ok) {
          const data = await response.json();
          setWorkerStatus(data.data);
        }
      } catch (error) {
        // Worker not available
        setWorkerStatus(null);
      }
    };

    checkWorkerStatus();
    const interval = setInterval(checkWorkerStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Handle escape key to close mobile sidebar
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Handle clicks outside sidebar to close it
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const sidebar = document.getElementById('sidebar');
      // Check if the click was outside the sidebar and the menu button
      if (sidebar && !sidebar.contains(e.target) && 
          !e.target.closest('#menu-button')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    // In a real implementation, this would apply theme changes
    document.documentElement.classList.toggle('light-mode');
  };

  // Handle logout with confirmation
  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        if (typeof onLogout === 'function') {
          await onLogout();
        } else {
          console.error('onLogout function not provided to ResponsiveSidebar');
        }
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }
  };

  // Get user display data
  const displayName = user ? user.firstName || 'User' : 'User';
  const displayPlan = user ? user.subscriptionPlan || 'Free' : 'Free';
  const displayInitial = displayName.charAt(0).toUpperCase();

  // Check if user is admin (you can implement your own admin check logic)
  const isAdmin = user?.role === 'admin' || user?.isAdmin;

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-30">
        <button 
          id="menu-button"
          onClick={toggleSidebar}
          className="p-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-600"
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-20 transition-opacity duration-300"
          aria-hidden="true"
        ></div>
      )}

      {/* Sidebar */}
      <div 
        id="sidebar"
        className={`
          ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0
          fixed lg:relative top-0 left-0 h-full z-20
          w-64 bg-black text-white
          transform transition-transform duration-300 ease-in-out
          flex flex-col
        `}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="p-6">
          <Logo />
        </div>
        
        <nav className="mt-6 flex-grow">
  <SidebarLink 
    icon={<BarChart size={20} />} 
    text={t('dashboard')}
    active={activeTab === 'dashboard'} 
    onClick={() => {
      setActiveTab('dashboard');
      if (windowWidth < 1024) setIsOpen(false);
    }} 
  />
  <SidebarLink 
    icon={<Film size={20} />} 
    text={t('myRenders')}
    active={activeTab === 'renders'} 
    onClick={() => {
      setActiveTab('renders');
      if (windowWidth < 1024) setIsOpen(false);
    }} 
  />
  <SidebarLink 
    icon={<Upload size={20} />} 
    text={t('newRender')}
    active={activeTab === 'upload'} 
    onClick={() => {
      setActiveTab('upload');
      if (windowWidth < 1024) setIsOpen(false);
    }} 
  />
  
  <SidebarLink 
    icon={<Settings size={20} />} 
    text={t('settings')}
    active={activeTab === 'settings'} 
    onClick={() => {
      setActiveTab('settings');
      if (windowWidth < 1024) setIsOpen(false);
    }} 
  />

  {/* Admin only links */}
  {user?.role === 'admin' && (
    <>
      <SidebarLink 
        icon={<Server size={20} />} 
        text={t('workerStatus')}
        active={activeTab === 'workers'} 
        onClick={() => {
          setActiveTab('workers');
          if (windowWidth < 1024) setIsOpen(false);
        }} 
      />
      <SidebarLink 
        icon={<CreditCard size={20} />} 
        text={t('paymentVerification')}
        active={activeTab === 'payments'} 
        onClick={() => {
          setActiveTab('payments');
          if (windowWidth < 1024) setIsOpen(false);
        }} 
      />
    </>
  )}
</nav>
        
        {/* Worker Status Indicator */}
        {workerStatus && (
          <div className="px-6 mb-4">
            <div className="border-t border-gray-800 pt-4">
              <div className={`flex items-center px-3 py-2 rounded-lg text-sm ${
                workerStatus.isRunning 
                  ? 'bg-green-900/30 text-green-400' 
                  : 'bg-red-900/30 text-red-400'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${
                  workerStatus.isRunning ? 'bg-green-400' : 'bg-red-400'
                }`}></div>
                <span className="text-xs">
                  Worker {workerStatus.isRunning ? 'Running' : 'Stopped'}
                </span>
                {workerStatus.isRunning && workerStatus.activeRenders > 0 && (
                  <span className="ml-auto text-xs font-medium">
                    {workerStatus.activeRenders}/{workerStatus.maxConcurrentRenders}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Language Switcher Section */}
        <div className="px-6 mb-4">
          <div className="border-t border-gray-800 pt-4 mb-4">
            <h4 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
              Language / 语言
            </h4>
            <LanguageSwitcher variant="default" className="w-full justify-between" />
          </div>
        </div>
              
        {/* User section */}
        <div className="border-t border-gray-800 p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
              <span className="text-lg font-medium">{displayInitial}</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-gray-500">
                {displayPlan} Plan
                {isAdmin && <span className="ml-1 text-red-400">• Admin</span>}
              </p>
            </div>
          </div>
          <button 
            className="mt-4 flex items-center text-gray-500 hover:text-white text-sm transition-colors w-full"
            onClick={handleLogout}
          >
            <LogOut size={16} className="mr-2" /> Sign Out
          </button>
        </div>
      </div>

      {/* Main content padding for mobile */}
      <div className="lg:hidden h-16"></div>
    </>
  );
};

export default ResponsiveSidebar;